import React from 'react';
import Hint, { HintTitle, HintText } from '@components/basics/hint';
import Grid from '@components/basics/grid';
import { Translate } from 'react-localize-redux';
import Flex from '@components/layout/flex';
import styled, { useTheme } from 'styled-components';
import moment from 'moment';
import Typography from '@components/basics/typography';
import CircleLoading from '@components/basics/circle-loading';
import CatalogRelease from '@common/types/catalog/catalog-release';

interface OwnershipLockHintProps {
  activeRelease?: CatalogRelease;
  reassignSplitError: boolean;
}

export const OwnershipLockHint: React.FC<OwnershipLockHintProps> = ({
  activeRelease,
  reassignSplitError,
}) => {
  const theme = useTheme();

  const [differenceDuration, setDifferenceDuration] = React.useState({
    days: 0,
    hours: 0,
    min: 0,
    sec: 0,
  });
  const [periodEnd, setPeriodEnd] = React.useState(1);

  React.useEffect(() => {
    const secondInterval = setInterval(() => {
      if (activeRelease?.release_date) {
        let differenceDuration = moment(
          moment(activeRelease?.release_date, 'YYYY-MM-DD').add(30, 'days')
        ).diff(moment(), 'seconds');
        const [sec, min, hours, days] = [
          Math.floor(differenceDuration % 60),
          Math.floor((differenceDuration % 3600) / 60),
          Math.floor((differenceDuration % (3600 * 24)) / 3600),
          Math.floor(differenceDuration / (3600 * 24)),
        ];
        setDifferenceDuration({ days, hours, min, sec });
        setPeriodEnd(differenceDuration);
      }
    }, 1000);

    return () => {
      clearInterval(secondInterval);
    };
  }, [activeRelease]);

  return (
    <Hint
      error={reassignSplitError}
      noBorderTop
      noTitle
      align="left"
      customContent={
        <Flex justifyContent="space-between" alignItems="center" gap={20}>
          <Grid>
            <HintTitle>
              <Translate
                id={
                  reassignSplitError
                    ? 'OWNERSHIP.HINT.ERROR.SPLITS.TITLE'
                    : periodEnd <= 0
                    ? 'OWNERSHIP.HINT.TITLE.LOCKED'
                    : 'OWNERSHIP.HINT.TITLE'
                }
                data={{
                  time: `${differenceDuration.days}d ${differenceDuration.hours}h ${differenceDuration.min}m ${differenceDuration.sec}s`,
                }}
              />
            </HintTitle>
            <HintText>
              <Translate
                id={
                  reassignSplitError
                    ? 'OWNERSHIP.HINT.ERROR.SPLITS.TEXT'
                    : periodEnd <= 0
                    ? 'OWNERSHIP.HINT.TEXT.LOCKED'
                    : 'OWNERSHIP.HINT.TEXT'
                }
              />
            </HintText>
          </Grid>
          {periodEnd > 0 && (
            <LoadingWrapper gap={4}>
              <CircleLoading />
              <DurationType type="small" color={theme.colors.tertiary}>
                {differenceDuration.days}d {differenceDuration.hours}h{' '}
                {differenceDuration.min}m
              </DurationType>
            </LoadingWrapper>
          )}
        </Flex>
      }
    ></Hint>
  );
};

const DurationType = styled(Typography)`
  width: 90px;
  text-align: center;
`;

const LoadingWrapper = styled(Grid)`
  place-items: center;
`;
